export const APPROVED_EMAIL_DOMAINS = ['tgh.org', 'omnicell.com'];

export const APPROVED_EMAIL_DOMAIN_MESSAGE =
  'Email sharing is restricted to recipients with @tgh.org or @omnicell.com email addresses.';

export interface EmailRecipientValidation {
  recipients: string[];
  invalidRecipients: string[];
  unapprovedRecipients: string[];
  isValid: boolean;
  isReadyForFeedback: boolean;
  message: string;
}

export function parseEmailRecipients(recipients: string | string[]): string[] {
  const recipientValues = Array.isArray(recipients) ? recipients : [recipients];

  return recipientValues
    .reduce((allRecipients: string[], recipient) => {
      if (!recipient) {
        return allRecipients;
      }

      return allRecipients.concat(String(recipient).split(/[;,]/));
    }, [])
    .map((recipient) => recipient.trim())
    .filter((recipient) => recipient.length > 0);
}

export function isValidEmailAddress(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

export function isEmailReadyForValidation(email: string): boolean {
  const trimmedEmail = (email || '').trim();
  const atIndex = trimmedEmail.lastIndexOf('@');
  const dotIndex = trimmedEmail.lastIndexOf('.');

  return atIndex > 0 && dotIndex > atIndex + 1 && trimmedEmail.length - dotIndex - 1 >= 3;
}

export function isApprovedOrganizationEmail(email: string): boolean {
  if (!isValidEmailAddress(email)) {
    return false;
  }

  const normalizedEmail = email.trim().toLowerCase();
  const atIndex = normalizedEmail.lastIndexOf('@');

  if (atIndex <= 0 || atIndex === normalizedEmail.length - 1) {
    return false;
  }

  return APPROVED_EMAIL_DOMAINS.includes(normalizedEmail.slice(atIndex + 1));
}

export function getUnapprovedEmailRecipients(recipients: string | string[]): string[] {
  return parseEmailRecipients(recipients).filter(
    (recipient) => !isApprovedOrganizationEmail(recipient)
  );
}

export function validateEmailRecipients(recipients: string | string[]): EmailRecipientValidation {
  const parsedRecipients = parseEmailRecipients(recipients);
  const isReadyForFeedback =
    parsedRecipients.length > 0 && parsedRecipients.every((recipient) => isEmailReadyForValidation(recipient));
  const invalidRecipients = parsedRecipients.filter((recipient) => !isValidEmailAddress(recipient));
  const unapprovedRecipients = parsedRecipients.filter(
    (recipient) => isValidEmailAddress(recipient) && !isApprovedOrganizationEmail(recipient)
  );

  let message = '';

  if (parsedRecipients.length === 0) {
    message = 'Please enter the recipient email.';
  } else if (invalidRecipients.length > 0) {
    message = `Please enter valid recipient emails. Remove or correct: ${invalidRecipients.join(', ')}`;
  } else if (unapprovedRecipients.length > 0) {
    message = `${APPROVED_EMAIL_DOMAIN_MESSAGE} Remove or correct: ${unapprovedRecipients.join(', ')}`;
  }

  return {
    recipients: parsedRecipients,
    invalidRecipients,
    unapprovedRecipients,
    isValid: parsedRecipients.length > 0 && invalidRecipients.length === 0 && unapprovedRecipients.length === 0,
    isReadyForFeedback,
    message
  };
}
